import * as spine from "@esotericsoftware/spine-webgl";
import { SpinePreviewApp, SkeletonInstance } from "../../../../../lib/SpinePreviewApp";
import { getTemplateOrder } from "../../../../../types/spine-template";
import {
  zIndexForSpineCanvas,
  type StaticStackItem,
} from "../../../../../lib/staticLayerStack";

const { Physics } = spine;

export interface ExportFramesOptions {
  app: SpinePreviewApp;
  spineCanvas: spine.SpineCanvas;
  outputSize?: number;
  fps?: number;
  durationMs?: number;
  /** 上限帧数；在整段动画时长内均匀采样，用于缩略动图 */
  maxFrames?: number;
  /** 透明背景（默认 false，填充 #f5f5f5） */
  transparentBackground?: boolean;
  onFrameProgress?: (percent: number) => void;
  /** 与编辑器预览一致的静态叠层（仅 kind:img），导出时画在 Spine 下层/上层 */
  staticStackItems?: StaticStackItem[];
  /** 头像混合预览：静态层 URL（已按 character 上下分好组） */
  staticUrlsBelow?: string[];
  staticUrlsAbove?: string[];
  /** 头像混合预览：Konva 背景 / 角色层（画在 Spine 下或上） */
  konvaUnderlay?: HTMLCanvasElement | null;
  konvaOverlay?: HTMLCanvasElement | null;
  /** 1024 透明高品质：离屏分辨率渲染 + 直读 alpha，不影响 512 标准路径 */
  highQualityCapture?: boolean;
  /** VAP 导出：采帧前额外推进若干「整段时长」圈数以稳定物理态，结束后会再次 reset 到 t=0 */
  warmupCycles?: number;
  /** 头像混合：人物 Konva 之上的关联 Spine */
  spineAboveApp?: SpinePreviewApp | null;
  spineAboveCanvas?: spine.SpineCanvas | null;
  /** 为 false 时不画主 Spine 槽（仅上方 Spine，如只有 bgEffect 关联） */
  spineBelowRender?: boolean;
}

function loadImageCors(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () =>
      reject(new Error(`静态图加载失败（可能跨域）: ${url.slice(0, 80)}`));
    img.src = url;
  });
}

function drawImageContain(
  ctx: CanvasRenderingContext2D,
  img: CanvasImageSource,
  w: number,
  h: number,
) {
  const iw =
    "naturalWidth" in img ? img.naturalWidth : (img as HTMLCanvasElement).width;
  const ih =
    "naturalHeight" in img
      ? img.naturalHeight
      : (img as HTMLCanvasElement).height;
  if (!iw || !ih) return;
  const scale = Math.min(w / iw, h / ih);
  const dw = iw * scale;
  const dh = ih * scale;
  const x = (w - dw) / 2;
  const y = (h - dh) / 2;
  ctx.drawImage(img, x, y, dw, dh);
}

interface StaticCompositeContext {
  preloaded: Map<string, HTMLImageElement>;
  below: { url: string; zIndex: number }[];
  above: { url: string; zIndex: number }[];
}

export interface FrameResult {
  canvas: HTMLCanvasElement;
  frameDelay: number;
}

export function getAnimationDuration(app: SpinePreviewApp): number {
  let maxDuration = 0;
  for (const inst of app.instances) {
    for (let i = 0; i < inst.state.tracks.length; i++) {
      const track = inst.state.tracks[i];
      if (track?.animation) {
        maxDuration = Math.max(maxDuration, track.animation.duration * 1000);
      }
    }
  }
  return maxDuration > 0 ? maxDuration : 2000;
}

export function sortInstances(instances: SkeletonInstance[]) {
  return [...instances].sort((a, b) => {
    return getTemplateOrder(a.type, a.isMain) - getTemplateOrder(b.type, b.isMain);
  });
}

/** 与 SpinePreviewApp.render 一致的绘制顺序，仅高品质导出使用 */
function sortInstancesLikePreview(app: SpinePreviewApp) {
  const indexInRelatedOrder = (templateId: string) => {
    const i = app.relatedTemplateIdsOrder.indexOf(templateId);
    return i === -1 ? app.relatedTemplateIdsOrder.length + 1 : i;
  };
  return [...app.instances].sort((a, b) => {
    const orderA = getTemplateOrder(a.type, a.isMain);
    const orderB = getTemplateOrder(b.type, b.isMain);
    if (orderA !== orderB) return orderA - orderB;
    const tieA = a.isMain ? -1 : indexInRelatedOrder(a.id);
    const tieB = b.isMain ? -1 : indexInRelatedOrder(b.id);
    return tieA - tieB;
  });
}

interface CanvasRenderTargetSnapshot {
  width: number;
  height: number;
}

function resizeSpineCanvasForExport(
  spineCanvas: spine.SpineCanvas,
  outputSize: number,
): CanvasRenderTargetSnapshot {
  const htmlCanvas = spineCanvas.htmlCanvas;
  const gl = spineCanvas.gl;
  const saved = { width: htmlCanvas.width, height: htmlCanvas.height };

  if (htmlCanvas.width !== outputSize || htmlCanvas.height !== outputSize) {
    htmlCanvas.width = outputSize;
    htmlCanvas.height = outputSize;
    gl.viewport(0, 0, outputSize, outputSize);
    const camera = spineCanvas.renderer.camera;
    camera.viewportWidth = outputSize;
    camera.viewportHeight = outputSize;
    spineCanvas.renderer.resize(1);
  }

  return saved;
}

function restoreSpineCanvasRenderTarget(
  spineCanvas: spine.SpineCanvas,
  saved: CanvasRenderTargetSnapshot,
) {
  const htmlCanvas = spineCanvas.htmlCanvas;
  if (
    htmlCanvas.width === saved.width &&
    htmlCanvas.height === saved.height
  ) {
    return;
  }

  const gl = spineCanvas.gl;
  htmlCanvas.width = saved.width;
  htmlCanvas.height = saved.height;
  gl.viewport(0, 0, saved.width, saved.height);
  const camera = spineCanvas.renderer.camera;
  camera.viewportWidth = saved.width;
  camera.viewportHeight = saved.height;
  spineCanvas.renderer.resize(1);
}

function snapshotAnimations(instances: SkeletonInstance[]) {
  return instances.map((inst) => {
    const track = inst.state.getCurrent(0);
    return {
      id: inst.id,
      animName: track?.animation?.name ?? null,
      trackTime: track?.trackTime ?? 0,
    };
  });
}

function resetAnimationsToStart(instances: SkeletonInstance[]) {
  for (const inst of instances) {
    const track = inst.state.getCurrent(0);
    if (track) {
      track.trackTime = 0;
    }
    inst.state.apply(inst.skeleton);
    inst.skeleton.updateWorldTransform(Physics.reset);
  }
}

function restoreAnimations(
  app: SpinePreviewApp,
  snapshots: ReturnType<typeof snapshotAnimations>,
) {
  for (const snap of snapshots) {
    const inst = app.instances.find((i) => i.id === snap.id);
    if (!inst || !snap.animName) continue;
    inst.state.setAnimation(0, snap.animName, true);
    const track = inst.state.getCurrent(0);
    if (track) {
      track.trackTime = snap.trackTime;
    }
    inst.state.apply(inst.skeleton);
    inst.skeleton.updateWorldTransform(Physics.update);
  }

  app.updateSkinsInternal();
  app.lastSelectedSkinsKey = "";
  app.lastToningColorsKey = "";
}

function renderSpineSortedToTemp(
  spineCanvas: spine.SpineCanvas,
  app: SpinePreviewApp,
  sorted: SkeletonInstance[],
  gl: WebGLRenderingContext,
  canvasW: number,
  canvasH: number,
  pixels: Uint8Array | null,
  tempCanvas: HTMLCanvasElement,
  tempCtx: CanvasRenderingContext2D,
  highQualityCapture: boolean,
) {
  const htmlCanvas = spineCanvas.htmlCanvas;
  const renderer = spineCanvas.renderer;
  const camera = renderer.camera;
  const offset = app.bounds.offset;
  const boundsSize = app.bounds.size;

  renderer.resize(1);
  camera.position.x = offset.x + boundsSize.x / 2;
  camera.position.y = offset.y + boundsSize.y / 2;
  const zoomX = boundsSize.x / htmlCanvas.width;
  const zoomY = boundsSize.y / htmlCanvas.height;
  camera.zoom = Math.max(zoomX, zoomY);
  camera.update();

  spineCanvas.clear(0, 0, 0, 0);
  renderer.begin();
  for (const inst of sorted) {
    renderer.drawSkeleton(inst.skeleton, false);
  }
  renderer.end();

  if (highQualityCapture) {
    // 透明 + 加色混合：用 canvas 直读，避免 readPixels 反预乘导致白边/方块
    gl.finish();
    tempCtx.clearRect(0, 0, canvasW, canvasH);
    tempCtx.drawImage(htmlCanvas, 0, 0, canvasW, canvasH);
  } else if (pixels) {
    gl.readPixels(0, 0, canvasW, canvasH, gl.RGBA, gl.UNSIGNED_BYTE, pixels);

    const imageData = tempCtx.createImageData(canvasW, canvasH);
    for (let y = 0; y < canvasH; y++) {
      const srcRow = (canvasH - 1 - y) * canvasW * 4;
      const dstRow = y * canvasW * 4;
      for (let x = 0; x < canvasW; x++) {
        const si = srcRow + x * 4;
        const di = dstRow + x * 4;
        const a = pixels[si + 3];
        if (a === 0) {
          imageData.data[di] = 0;
          imageData.data[di + 1] = 0;
          imageData.data[di + 2] = 0;
          imageData.data[di + 3] = 0;
        } else if (a === 255) {
          imageData.data[di] = pixels[si];
          imageData.data[di + 1] = pixels[si + 1];
          imageData.data[di + 2] = pixels[si + 2];
          imageData.data[di + 3] = 255;
        } else {
          const inv = 255 / a;
          imageData.data[di] = Math.min(255, (pixels[si] * inv) | 0);
          imageData.data[di + 1] = Math.min(255, (pixels[si + 1] * inv) | 0);
          imageData.data[di + 2] = Math.min(255, (pixels[si + 2] * inv) | 0);
          imageData.data[di + 3] = a;
        }
      }
    }
    tempCtx.putImageData(imageData, 0, 0);
  }

  return tempCanvas;
}

function scaleSpineTempToOutput(
  tempCanvas: HTMLCanvasElement,
  canvasW: number,
  canvasH: number,
  outputSize: number,
  highQualityCapture: boolean,
): HTMLCanvasElement {
  if (highQualityCapture && canvasW === outputSize && canvasH === outputSize) {
    return tempCanvas;
  }
  const spineOnly = document.createElement("canvas");
  spineOnly.width = outputSize;
  spineOnly.height = outputSize;
  const spineCtx = spineOnly.getContext("2d")!;
  spineCtx.clearRect(0, 0, outputSize, outputSize);
  spineCtx.drawImage(
    tempCanvas,
    0,
    0,
    canvasW,
    canvasH,
    0,
    0,
    outputSize,
    outputSize,
  );
  return spineOnly;
}

function captureFrame(
  spineCanvas: spine.SpineCanvas,
  app: SpinePreviewApp,
  sorted: SkeletonInstance[],
  gl: WebGLRenderingContext,
  canvasW: number,
  canvasH: number,
  pixels: Uint8Array,
  outCtx: CanvasRenderingContext2D,
  outputSize: number,
  tempCanvas: HTMLCanvasElement,
  tempCtx: CanvasRenderingContext2D,
  staticComposite: StaticCompositeContext | null,
  transparentBackground: boolean,
  highQualityCapture: boolean,
  konvaUnderlay?: HTMLCanvasElement | null,
  konvaOverlay?: HTMLCanvasElement | null,
  spineAbove?: {
    app: SpinePreviewApp;
    spineCanvas: spine.SpineCanvas;
    sorted: SkeletonInstance[];
    gl: WebGLRenderingContext;
    canvasW: number;
    canvasH: number;
    pixels: Uint8Array | null;
    tempCanvas: HTMLCanvasElement;
    tempCtx: CanvasRenderingContext2D;
  } | null,
) {
  if (!sorted.length) {
    // 仅上方 Spine 时主槽为空，跳过下方渲染
  } else {
    renderSpineSortedToTemp(
      spineCanvas,
      app,
      sorted,
      gl,
      canvasW,
      canvasH,
      pixels,
      tempCanvas,
      tempCtx,
      highQualityCapture,
    );
  }

  const spineLayer =
    sorted.length > 0
      ? scaleSpineTempToOutput(
          tempCanvas,
          canvasW,
          canvasH,
          outputSize,
          highQualityCapture,
        )
      : null;

  let spineAboveLayer: HTMLCanvasElement | null = null;
  if (spineAbove && spineAbove.sorted.length > 0) {
    renderSpineSortedToTemp(
      spineAbove.spineCanvas,
      spineAbove.app,
      spineAbove.sorted,
      spineAbove.gl,
      spineAbove.canvasW,
      spineAbove.canvasH,
      spineAbove.pixels,
      spineAbove.tempCanvas,
      spineAbove.tempCtx,
      highQualityCapture,
    );
    spineAboveLayer = scaleSpineTempToOutput(
      spineAbove.tempCanvas,
      spineAbove.canvasW,
      spineAbove.canvasH,
      outputSize,
      highQualityCapture,
    );
  }

  if (transparentBackground) {
    outCtx.clearRect(0, 0, outputSize, outputSize);
  } else {
    outCtx.fillStyle = "#f5f5f5";
    outCtx.fillRect(0, 0, outputSize, outputSize);
  }

  if (konvaUnderlay) {
    drawImageContain(outCtx, konvaUnderlay, outputSize, outputSize);
  }

  if (
    staticComposite &&
    (staticComposite.below.length > 0 || staticComposite.above.length > 0)
  ) {
    const { preloaded, below, above } = staticComposite;
    for (const { url, zIndex } of [...below].sort(
      (a, b) => a.zIndex - b.zIndex,
    )) {
      const img = preloaded.get(url);
      if (img) drawImageContain(outCtx, img, outputSize, outputSize);
    }
    if (spineLayer) outCtx.drawImage(spineLayer, 0, 0);
    if (konvaOverlay) {
      drawImageContain(outCtx, konvaOverlay, outputSize, outputSize);
    }
    // 前景静态图（如 fgStatic / 角色主体 Konva）应位于角色之上、上方 Spine（fg/fgEffect）之下
    for (const { url, zIndex } of [...above].sort(
      (a, b) => a.zIndex - b.zIndex,
    )) {
      const img = preloaded.get(url);
      if (img) drawImageContain(outCtx, img, outputSize, outputSize);
    }
    if (spineAboveLayer) outCtx.drawImage(spineAboveLayer, 0, 0);
  } else {
    if (spineLayer) outCtx.drawImage(spineLayer, 0, 0);
    if (konvaOverlay) {
      drawImageContain(outCtx, konvaOverlay, outputSize, outputSize);
    }
    if (spineAboveLayer) outCtx.drawImage(spineAboveLayer, 0, 0);
  }
}

/**
 * 暂停 SpineCanvas 的 rAF 循环中的 update/render。
 * 返回恢复函数。
 */
export function pauseSpineCanvas(spineCanvas: spine.SpineCanvas) {
  const config = (spineCanvas as any).config;
  const origUpdate = config.app.update;
  const origRender = config.app.render;
  config.app.update = () => {};
  config.app.render = () => {};
  return () => {
    config.app.update = origUpdate;
    config.app.render = origRender;
  };
}

/**
 * 逐帧渲染 Spine 动画，以异步批处理方式生成帧画面。
 * 返回所有帧的 canvas 快照和帧延迟。
 */
export function captureAllFrames({
  app,
  spineCanvas,
  outputSize = 512,
  fps = 50,
  durationMs,
  maxFrames,
  transparentBackground = false,
  onFrameProgress,
  staticStackItems,
  staticUrlsBelow,
  staticUrlsAbove,
  konvaUnderlay,
  konvaOverlay,
  highQualityCapture = false,
  warmupCycles = 0,
  spineAboveApp,
  spineAboveCanvas,
  spineBelowRender = true,
}: ExportFramesOptions): Promise<{ frames: FrameResult[]; totalDuration: number }> {
  return new Promise((resolve, reject) => {
    void (async () => {
      try {
        let staticComposite: StaticCompositeContext | null = null;
        if (staticUrlsBelow?.length || staticUrlsAbove?.length) {
          const urls = [
            ...new Set([...(staticUrlsBelow || []), ...(staticUrlsAbove || [])]),
          ];
          const preloaded = new Map<string, HTMLImageElement>();
          await Promise.all(
            urls.map(async (url) => {
              const img = await loadImageCors(url);
              preloaded.set(url, img);
            }),
          );
          staticComposite = {
            preloaded,
            below: (staticUrlsBelow || []).map((url, i) => ({
              url,
              zIndex: i,
            })),
            above: (staticUrlsAbove || []).map((url, i) => ({
              url,
              zIndex: i,
            })),
          };
        } else if (staticStackItems?.length) {
          const imgs = staticStackItems.filter(
            (i): i is Extract<StaticStackItem, { kind: "img" }> =>
              i.kind === "img",
          );
          const canvasZ = zIndexForSpineCanvas();
          const below: { url: string; zIndex: number }[] = [];
          const above: { url: string; zIndex: number }[] = [];
          for (const item of imgs) {
            if (item.zIndex < canvasZ) {
              below.push({ url: item.url, zIndex: item.zIndex });
            } else if (item.zIndex > canvasZ) {
              above.push({ url: item.url, zIndex: item.zIndex });
            } else {
              below.push({ url: item.url, zIndex: item.zIndex });
            }
          }
          const urls = [...new Set(imgs.map((i) => i.url))];
          const preloaded = new Map<string, HTMLImageElement>();
          await Promise.all(
            urls.map(async (url) => {
              const img = await loadImageCors(url);
              preloaded.set(url, img);
            }),
          );
          staticComposite = { preloaded, below, above };
        }

        const hasAboveInstances = (spineAboveApp?.instances.length ?? 0) > 0;
        if (app.instances.length === 0 && !hasAboveInstances) {
          reject(new Error("没有可渲染的 Skeleton 实例"));
          return;
        }

        const duration = durationMs ?? getAnimationDuration(
          app.instances.length > 0 ? app : spineAboveApp!,
        );
        let frameDelay = Math.round(1000 / fps);
        let totalFrames = Math.max(1, Math.floor(duration / frameDelay));
        if (maxFrames != null && totalFrames > maxFrames) {
          totalFrames = maxFrames;
        }
        // 均分整段时长，保证 totalFrames * frameDelay === duration，循环首尾衔接
        frameDelay = duration / totalFrames;
        const dt = frameDelay / 1000;

        if (totalFrames <= 0) {
          reject(new Error("动画时长为 0，无法导出"));
          return;
        }

        const htmlCanvas = spineCanvas.htmlCanvas;
        const gl = spineCanvas.gl;
        const renderTargetSnapshot =
          highQualityCapture || !htmlCanvas.width || !htmlCanvas.height
            ? resizeSpineCanvasForExport(spineCanvas, outputSize)
            : null;
        const canvasW = htmlCanvas.width;
        const canvasH = htmlCanvas.height;
        if (!canvasW || !canvasH) {
          reject(
            new Error(
              `Spine 画布尺寸无效 (${canvasW}x${canvasH})，请确认关联模版已加载且预览可见`,
            ),
          );
          return;
        }
        const pixels = highQualityCapture
          ? null
          : new Uint8Array(canvasW * canvasH * 4);

        const tempCanvas = document.createElement("canvas");
        tempCanvas.width = canvasW;
        tempCanvas.height = canvasH;
        const tempCtx = tempCanvas.getContext("2d")!;

        const outCanvas = document.createElement("canvas");
        outCanvas.width = outputSize;
        outCanvas.height = outputSize;
        const outCtx = outCanvas.getContext("2d")!;
        outCtx.imageSmoothingEnabled = true;
        outCtx.imageSmoothingQuality = "high";

        const sorted = spineBelowRender
          ? highQualityCapture
            ? sortInstancesLikePreview(app)
            : sortInstances(app.instances)
          : [];
        const sortedAbove =
          spineAboveApp && spineAboveCanvas
            ? highQualityCapture
              ? sortInstancesLikePreview(spineAboveApp)
              : sortInstances(spineAboveApp.instances)
            : [];

        const animSnapshot = snapshotAnimations(app.instances);
        const animSnapshotAbove = spineAboveApp
          ? snapshotAnimations(spineAboveApp.instances)
          : null;
        const resumeSpineCanvas = pauseSpineCanvas(spineCanvas);
        const resumeSpineAboveCanvas =
          spineAboveCanvas && spineAboveApp
            ? pauseSpineCanvas(spineAboveCanvas)
            : null;

        resetAnimationsToStart(app.instances);
        if (spineAboveApp) resetAnimationsToStart(spineAboveApp.instances);
        if (highQualityCapture) {
          app.applyToning();
          spineAboveApp?.applyToning();
        }

        if (warmupCycles > 0) {
          const warmupSteps = totalFrames * warmupCycles;
          for (let w = 0; w < warmupSteps; w++) {
            for (const inst of app.instances) {
              inst.skeleton.update(dt);
              inst.state.update(dt);
              inst.state.apply(inst.skeleton);
              inst.skeleton.updateWorldTransform(Physics.update);
            }
            if (spineAboveApp) {
              for (const inst of spineAboveApp.instances) {
                inst.skeleton.update(dt);
                inst.state.update(dt);
                inst.state.apply(inst.skeleton);
                inst.skeleton.updateWorldTransform(Physics.update);
              }
            }
          }
          resetAnimationsToStart(app.instances);
          if (spineAboveApp) resetAnimationsToStart(spineAboveApp.instances);
          if (highQualityCapture) {
            app.applyToning();
            spineAboveApp?.applyToning();
          }
        }

        let aboveRenderTargetSnapshot: CanvasRenderTargetSnapshot | null = null;
        let aboveCanvasW = 0;
        let aboveCanvasH = 0;
        let abovePixels: Uint8Array | null = null;
        let aboveTempCanvas: HTMLCanvasElement | null = null;
        let aboveTempCtx: CanvasRenderingContext2D | null = null;
        let aboveGl: WebGLRenderingContext | null = null;

        if (spineAboveCanvas && spineAboveApp && sortedAbove.length > 0) {
          const aboveHtml = spineAboveCanvas.htmlCanvas;
          aboveRenderTargetSnapshot =
            highQualityCapture || !aboveHtml.width || !aboveHtml.height
              ? resizeSpineCanvasForExport(spineAboveCanvas, outputSize)
              : null;
          aboveCanvasW = aboveHtml.width;
          aboveCanvasH = aboveHtml.height;
          if (!aboveCanvasW || !aboveCanvasH) {
            reject(new Error("Spine 上方画布尺寸无效"));
            return;
          }
          abovePixels = highQualityCapture
            ? null
            : new Uint8Array(aboveCanvasW * aboveCanvasH * 4);
          aboveTempCanvas = document.createElement("canvas");
          aboveTempCanvas.width = aboveCanvasW;
          aboveTempCanvas.height = aboveCanvasH;
          aboveTempCtx = aboveTempCanvas.getContext("2d")!;
          aboveGl = spineAboveCanvas.gl;
        }

        const frames: FrameResult[] = [];
        const BATCH_SIZE = 5;
        let frameIndex = 0;

        function cleanup() {
          if (renderTargetSnapshot) {
            restoreSpineCanvasRenderTarget(spineCanvas, renderTargetSnapshot);
          }
          if (aboveRenderTargetSnapshot && spineAboveCanvas) {
            restoreSpineCanvasRenderTarget(
              spineAboveCanvas,
              aboveRenderTargetSnapshot,
            );
          }
          restoreAnimations(app, animSnapshot);
          if (spineAboveApp && animSnapshotAbove) {
            restoreAnimations(spineAboveApp, animSnapshotAbove);
          }
          resumeSpineCanvas();
          resumeSpineAboveCanvas?.();
        }

        function processBatch() {
          try {
            const batchEnd = Math.min(frameIndex + BATCH_SIZE, totalFrames);
            for (; frameIndex < batchEnd; frameIndex++) {
              onFrameProgress?.(frameIndex / totalFrames);

              if (frameIndex > 0) {
                for (const inst of app.instances) {
                  inst.skeleton.update(dt);
                  inst.state.update(dt);
                  inst.state.apply(inst.skeleton);
                  inst.skeleton.updateWorldTransform(Physics.update);
                }
                if (spineAboveApp) {
                  for (const inst of spineAboveApp.instances) {
                    inst.skeleton.update(dt);
                    inst.state.update(dt);
                    inst.state.apply(inst.skeleton);
                    inst.skeleton.updateWorldTransform(Physics.update);
                  }
                }
              }

              if (highQualityCapture) {
                app.applyToning();
                spineAboveApp?.applyToning();
              }

              captureFrame(
                spineCanvas,
                app,
                sorted,
                gl,
                canvasW,
                canvasH,
                pixels ?? new Uint8Array(0),
                outCtx,
                outputSize,
                tempCanvas,
                tempCtx,
                staticComposite,
                transparentBackground,
                highQualityCapture,
                konvaUnderlay,
                konvaOverlay,
                spineAboveApp &&
                  spineAboveCanvas &&
                  aboveTempCanvas &&
                  aboveTempCtx &&
                  aboveGl &&
                  sortedAbove.length > 0
                  ? {
                      app: spineAboveApp,
                      spineCanvas: spineAboveCanvas,
                      sorted: sortedAbove,
                      gl: aboveGl,
                      canvasW: aboveCanvasW,
                      canvasH: aboveCanvasH,
                      pixels: abovePixels,
                      tempCanvas: aboveTempCanvas,
                      tempCtx: aboveTempCtx,
                    }
                  : null,
              );

              const snapshot = document.createElement("canvas");
              snapshot.width = outputSize;
              snapshot.height = outputSize;
              snapshot.getContext("2d")!.drawImage(outCanvas, 0, 0);
              frames.push({
                canvas: snapshot,
                frameDelay: Math.max(1, Math.round(frameDelay)),
              });
            }

            if (frameIndex < totalFrames) {
              setTimeout(processBatch, 0);
            } else {
              cleanup();
              resolve({ frames, totalDuration: duration });
            }
          } catch (err) {
            cleanup();
            reject(err);
          }
        }

        setTimeout(processBatch, 50);
      } catch (err) {
        reject(err);
      }
    })();
  });
}

export interface CaptureStaticStackFramesOnlyOptions {
  staticStackItems: StaticStackItem[];
  outputSize?: number;
  fps?: number;
  /** 无 Spine 时重复静态合成帧的时长，默认 2s（与无动画时 getAnimationDuration 回退一致） */
  durationMs?: number;
  maxFrames?: number;
  transparentBackground?: boolean;
  onFrameProgress?: (percent: number) => void;
}

/**
 * 无主 Spine、仅静态图层时：合成一帧后按 fps/duration 复制为多帧，供 GIF/WebP 与 captureAllFrames 对齐。
 */
export async function captureStaticStackFramesOnly({
  staticStackItems,
  outputSize = 512,
  fps = 50,
  durationMs = 2000,
  maxFrames,
  transparentBackground = false,
  onFrameProgress,
}: CaptureStaticStackFramesOnlyOptions): Promise<{
  frames: FrameResult[];
  totalDuration: number;
}> {
  const imgs = staticStackItems.filter(
    (i): i is Extract<StaticStackItem, { kind: "img" }> => i.kind === "img",
  );
  if (imgs.length === 0) {
    throw new Error("没有可导出的静态图层");
  }

  const urls = [...new Set(imgs.map((i) => i.url))];
  const preloaded = new Map<string, HTMLImageElement>();
  await Promise.all(
    urls.map(async (url) => {
      preloaded.set(url, await loadImageCors(url));
    }),
  );

  const sorted = [...imgs].sort((a, b) => a.zIndex - b.zIndex);
  const outCanvas = document.createElement("canvas");
  outCanvas.width = outputSize;
  outCanvas.height = outputSize;
  const outCtx = outCanvas.getContext("2d")!;
  outCtx.imageSmoothingEnabled = true;
  outCtx.imageSmoothingQuality = "high";
  if (transparentBackground) {
    outCtx.clearRect(0, 0, outputSize, outputSize);
  } else {
    outCtx.fillStyle = "#f5f5f5";
    outCtx.fillRect(0, 0, outputSize, outputSize);
  }
  for (const item of sorted) {
    const img = preloaded.get(item.url);
    if (img) drawImageContain(outCtx, img, outputSize, outputSize);
  }

  let frameDelay = Math.round(1000 / fps);
  let totalFrames = Math.max(1, Math.floor(durationMs / frameDelay));
  if (maxFrames != null && totalFrames > maxFrames) {
    totalFrames = maxFrames;
  }
  frameDelay = Math.max(1, Math.round(durationMs / totalFrames));

  const frames: FrameResult[] = [];
  for (let i = 0; i < totalFrames; i++) {
    onFrameProgress?.((i + 1) / totalFrames);
    const snapshot = document.createElement("canvas");
    snapshot.width = outputSize;
    snapshot.height = outputSize;
    snapshot.getContext("2d")!.drawImage(outCanvas, 0, 0);
    frames.push({ canvas: snapshot, frameDelay });
  }

  return { frames, totalDuration: durationMs };
}
